import { Box, TablePagination, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DomainIcon from "@mui/icons-material/Domain";
import { getToken } from "utils/getToken";
import useFetch from "hooks/useFetch";
import { useEffect, useState } from "react";
import useCautionStore from "store/cautionStore";
import { IListedDomain } from "interfaces/domains";
import AdminListedDomainsTab from "components/AdminListedDomainsTab";
import CustomButton from "components/Button";
import { toast } from "react-toastify";
import AdminListedDomainsModal from "components/AdminListedDomainsModal";
import CautionModal from "components/CautionModal";

const DomainsPage = () => {
  const token = getToken();
  const { t } = useTranslation();
  const { closeModal, handleDomainToDeleteChange, isDomainDeleteModalOpen, domainToDelete } = useCautionStore();
  const [page, setPage] = useState<number>(0);
  const rowsPerPage = 20;
  const [listedDomains, setListedDomains] = useState<IListedDomain[]>([]);
  const [isDomainsModalOpen, setIsDomainsModalOpen] = useState<boolean>(false);
  const currentListedDomains = listedDomains.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const { triggerFetch: getListedDomains } = useFetch({
    url: "admin/domains",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: checkDomainsLoading, triggerFetch: checkListedDomainsStatus } = useFetch({
    url: "admin/domains/cloudflare-activation-check",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: domainDeletionLoading, triggerFetch: deleteListedDomains } = useFetch({
    url: `admin/domains/${domainToDelete?.id}`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const getAllListedDomains = async () => {
    const { listedDomains } = await getListedDomains();
    const reversedListedDomains = [...listedDomains].reverse();
    setListedDomains(reversedListedDomains);
  };

  const checkInactiveListedDomainsStatus = async () => {
    try {
      const { listedDomains } = await checkListedDomainsStatus();
      const reversedListedDomains = [...listedDomains].reverse();
      setListedDomains(reversedListedDomains);
      toast.success(t("successDomainStatusCheck"));
    } catch (error) {
      toast.error(t("unsuccessfulDomainStatusCheck"));
    }
  };

  const handleListedDomainDelete = async () => {
    try {
      const res = await deleteListedDomains();

      if (!res.success) {
        handleDomainToDeleteChange(null);
        closeModal();
        toast.error(t("unsuccessfulDomainDeletion"));
        return;
      }

      await getAllListedDomains();
      handleDomainToDeleteChange(null);
      closeModal();
      toast.success(t("successDomainDeletion"));
    } catch (error) {
      toast.error(t("unsuccessfulDomainDeletion"));
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
  };

  useEffect(() => {
    void getAllListedDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          // alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <DomainIcon sx={{ width: "36px", height: "36px", color: "#6D6777" }} />
          <Typography
            sx={{
              fontFamily: "var(--cygreRegular), sans-serif",
              fontSize: "20px",
              fontWeight: 500,
              color: "#2E263D",
            }}
          >
            {t("domains")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "10px",
            width: { xs: "100%", sm: "370px" },
            marginTop: { xs: "16px", sm: 0 },
          }}
        >
          <CustomButton
            textButton={t("checkListedDomains")}
            height={40}
            width="100%"
            customColor="#6D6777"
            customBorderColor="#C9D0D8"
            customHoverColor="transparent"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={() => checkInactiveListedDomainsStatus()}
            loading={checkDomainsLoading}
          />
          <CustomButton
            textButton={t("addListedDomains")}
            height={40}
            width="100%"
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={() => setIsDomainsModalOpen(true)}
          />
        </Box>
      </Box>

      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={listedDomains.length}
        rowsPerPage={20}
        page={page}
        labelRowsPerPage={null}
        labelDisplayedRows={() => ""}
        onPageChange={handleChangePage}
        sx={{
          "& .MuiTablePagination-actions": { margin: "0px !important" },
          "& .MuiTablePagination-spacer": {
            display: "none",
          },
          "& .MuiTablePagination-toolbar": {
            padding: 0,
            justifyContent: "flex-start",
          },
        }}
      />
      <AdminListedDomainsTab
        listedDomains={currentListedDomains}
        page={page}
        totalListedDomains={listedDomains.length}
        handleChangePage={handleChangePage}
        getAllListedDomains={getAllListedDomains}
      />
      {isDomainsModalOpen && (
        <AdminListedDomainsModal
          open={isDomainsModalOpen}
          setIsModalOpen={setIsDomainsModalOpen}
          setListedDomains={setListedDomains}
        />
      )}
      {isDomainDeleteModalOpen && (
        <CautionModal handleConfirm={handleListedDomainDelete} isLoading={domainDeletionLoading} />
      )}
    </>
  );
};

export default DomainsPage;
